// Рецептуры (модуль recipes): список версий и карточка по форме управляемой
// рецептуры — состав, режим, подписи «разработал — согласовано — утверждено».
// Чтение — recipe.read; черновик, согласование, новая версия, архив —
// recipe.manage; утверждение — recipe.approve.  Правится только черновик.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { ApiError, manufacturingOrdersApi, recipesApi, type Recipe, type RecipeStatus } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { RecipeParam } from "@/lib/types";

const INPUT_CLS = "w-full px-2 py-1 rounded-md bg-surface border border-border";
const STATUS_PILL: Record<RecipeStatus, string> = { DRAFT: "pill-warn", APPROVED: "pill-ok", ARCHIVED: "" };

function useRecipeError() {
  const { t } = useTranslation();
  return (e: unknown) =>
    e instanceof ApiError
      ? t(`recipes.errors.${e.code}`, { ...(e.details ?? {}), defaultValue: e.message })
      : e instanceof Error
        ? e.message
        : String(e);
}

function useProcessName() {
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });
  return {
    processes: processes.data ?? [],
    name: (key: string) => processes.data?.find((p) => p.key === key)?.name ?? key,
  };
}

function StatusPill({ status }: { status: RecipeStatus }) {
  const { t } = useTranslation();
  return <span className={`pill text-xs ${STATUS_PILL[status]}`}>{t(`recipes.statuses.${status}`)}</span>;
}

export default function Recipes() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { name: processName } = useProcessName();
  const list = useQuery({ queryKey: ["recipes"], queryFn: () => recipesApi.list() });

  return (
    <>
      <PageHeader eyebrow={t("recipes.eyebrow")} title={t("recipes.title")} description={t("recipes.description")} />
      {hasPermission("recipe.manage") && <CreateForm />}
      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}
      {list.data && list.data.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">{t("recipes.empty")}</div>
      )}
      {list.data && list.data.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("recipes.code")}</th>
                <th className="px-3 py-2 text-left">{t("recipes.version")}</th>
                <th className="px-3 py-2 text-left">{t("recipes.name")}</th>
                <th className="px-3 py-2 text-left">{t("recipes.process")}</th>
                <th className="px-3 py-2 text-left">{t("recipes.status")}</th>
                <th className="px-3 py-2 text-left">{t("recipes.approvedBy")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/admin/recipes/${r.id}`} className="underline">
                      {r.code}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">v{r.version}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2">{processName(r.process_key)}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-text-muted">
                    {r.approved_at ? `${r.approver_name ?? "—"} · ${formatDateTime(r.approved_at)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function CreateForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const errText = useRecipeError();
  const { processes } = useProcessName();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [processKey, setProcessKey] = useState("");
  const key = processKey || (processes.length === 1 ? processes[0]!.key : "");
  const create = useMutation({
    mutationFn: () => recipesApi.create({ code: code.trim(), name: name.trim(), process_key: key }),
    onSuccess: (r) => navigate(`/admin/recipes/${r.id}`),
  });

  return (
    <section className="card p-4 mt-4 space-y-3">
      <h2 className="text-base">{t("recipes.new")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input className={INPUT_CLS} placeholder={t("recipes.code")} value={code} onChange={(e) => setCode(e.target.value)} />
        <input className={INPUT_CLS} placeholder={t("recipes.name")} value={name} onChange={(e) => setName(e.target.value)} />
        <select className={INPUT_CLS} value={key} onChange={(e) => setProcessKey(e.target.value)}>
          <option value="">{t("recipes.process")} —</option>
          {processes.map((p) => (
            <option key={p.key} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      {create.isError && <ErrorBanner onDismiss={() => create.reset()}>{errText(create.error)}</ErrorBanner>}
      <button
        className="btn btn-sm btn-primary"
        disabled={!code.trim() || !name.trim() || !key || create.isPending}
        onClick={() => create.mutate()}
      >
        {t("recipes.create")}
      </button>
    </section>
  );
}

export function RecipeDetail() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["recipes", id], queryFn: () => recipesApi.get(id) });
  return (
    <>
      <PageHeader
        eyebrow={t("recipes.eyebrow")}
        title={q.data ? `${q.data.code} · v${q.data.version}` : t("recipes.title")}
        description={q.data?.name}
        actions={
          <Link to="/admin/recipes" className="btn">
            {t("recipes.back")}
          </Link>
        }
      />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState error={q.error} />}
      {/* key: после сохранения форма берёт значения заново с сервера. */}
      {q.data && <RecipeCard key={q.data.updated_at} recipe={q.data} />}
    </>
  );
}

// Строки таблиц в форме — строками, числа разбираются при сохранении.
type ComponentRow = { component: string; grade: string; function: string; share: string; tolerance: string; spec: string };
type ParamRow = { key: string; label: string; value: string; control: string };

const parseNum = (s: string) => Number(s.trim().replace(",", "."));

function RecipeCard({ recipe }: { recipe: Recipe }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { name: processName } = useProcessName();
  const errText = useRecipeError();
  const canManage = hasPermission("recipe.manage");
  const editable = recipe.status === "DRAFT" && canManage;

  const [name, setName] = useState(recipe.name);
  const [note, setNote] = useState(recipe.note ?? "");
  const [rows, setRows] = useState<ComponentRow[]>(
    recipe.components.map((c) => ({
      component: c.component,
      grade: c.grade ?? "",
      function: c.function ?? "",
      share: String(c.share_pct),
      tolerance: c.tolerance != null ? String(c.tolerance) : "",
      spec: c.spec ?? "",
    })),
  );
  const [params, setParams] = useState<ParamRow[]>(
    recipe.params.map((p) => ({ key: p.key, label: p.label, value: p.value ?? "", control: p.control ?? "" })),
  );
  const [dirty, setDirty] = useState(false);
  const edit = <T,>(set: (fn: (v: T) => T) => void) => (fn: (v: T) => T) => {
    set(fn);
    setDirty(true);
  };
  const editRows = edit(setRows);
  const editParams = edit(setParams);

  const sum = rows.reduce((s, r) => s + (Number.isFinite(parseNum(r.share)) ? parseNum(r.share) : 0), 0);
  const badNumber = rows.some(
    (r) => !Number.isFinite(parseNum(r.share)) || r.share.trim() === "" || (r.tolerance.trim() !== "" && !Number.isFinite(parseNum(r.tolerance))),
  );

  const done = (r: Recipe) => {
    qc.invalidateQueries({ queryKey: ["recipes"] });
    if (r.id !== recipe.id) navigate(`/admin/recipes/${r.id}`);
  };
  const save = useMutation({
    mutationFn: () =>
      recipesApi.update(recipe.id, {
        name: name.trim(),
        note: note.trim() || null,
        components: rows
          .filter((r) => r.component.trim())
          .map((r) => ({
            component: r.component.trim(),
            grade: r.grade.trim() || null,
            function: r.function.trim() || null,
            share_pct: parseNum(r.share),
            tolerance: r.tolerance.trim() ? parseNum(r.tolerance) : null,
            spec: r.spec.trim() || null,
          })),
        params: params
          .filter((p) => p.label.trim())
          .map(
            (p): RecipeParam => ({
              key: p.key,
              label: p.label.trim(),
              value: p.value.trim() || null,
              control: p.control.trim() || null,
            }),
          ),
      }),
    onSuccess: done,
  });
  const action = useMutation({
    mutationFn: (kind: "agree" | "approve" | "newVersion" | "archive") => recipesApi[kind](recipe.id),
    onSuccess: done,
  });
  const error = save.error ?? action.error;
  const signature = (who: string | null, at: string | null) => (at ? `${who ?? "—"} · ${formatDateTime(at)}` : "—");

  return (
    <div className="space-y-4 mt-4">
      <section className="card p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={recipe.status} />
          <span className="pill pill-danger text-xs">{t("recipes.confidential")}</span>
          <span className="text-sm text-text-muted">
            {t("recipes.process")}: {processName(recipe.process_key)}
          </span>
        </div>
        <p className="text-xs text-text-muted">{t("recipes.formNote")}</p>
        {recipe.status !== "DRAFT" && <p className="text-xs text-text-muted">{t("recipes.draftOnly")}</p>}
        {editable && (
          <label className="block max-w-xl">
            <span className="text-xs text-text-muted">{t("recipes.name")}</span>
            <input
              className={INPUT_CLS}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
            />
          </label>
        )}
      </section>

      <section className="card overflow-x-auto">
        <h2 className="text-base px-4 pt-3">{t("recipes.components")}</h2>
        <table className="w-full text-sm mt-2">
          <thead className="bg-surface-alt text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t("recipes.col.no")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.component")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.grade")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.function")}</th>
              <th className="px-3 py-2 text-right">{t("recipes.col.share")}</th>
              <th className="px-3 py-2 text-right">{t("recipes.col.tolerance")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.spec")}</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{i + 1}</td>
                {(["component", "grade", "function", "share", "tolerance", "spec"] as const).map((f) => (
                  <td key={f} className={`px-3 py-2 ${f === "share" || f === "tolerance" ? "text-right font-mono" : ""}`}>
                    {editable ? (
                      <input
                        className={`${INPUT_CLS} ${f === "share" || f === "tolerance" ? "text-right font-mono" : ""}`}
                        inputMode={f === "share" || f === "tolerance" ? "decimal" : undefined}
                        value={r[f]}
                        onChange={(e) => editRows((rs) => rs.map((x, k) => (k === i ? { ...x, [f]: e.target.value } : x)))}
                      />
                    ) : f === "tolerance" && r[f] ? (
                      `±${r[f]}`
                    ) : (
                      r[f] || "—"
                    )}
                  </td>
                ))}
                {editable && (
                  <td className="px-2">
                    <button
                      className="btn btn-sm"
                      title={t("recipes.removeRow")}
                      onClick={() => editRows((rs) => rs.filter((_, k) => k !== i))}
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr className="border-t border-border">
              <td />
              <td className="px-3 py-2 text-text-muted" colSpan={3}>
                {t("recipes.sum")}
              </td>
              <td className={`px-3 py-2 text-right font-mono ${Math.abs(sum - 100) > 0.01 ? "text-danger" : ""}`}>
                {Math.round(sum * 1000) / 1000}
              </td>
              <td colSpan={editable ? 3 : 2} />
            </tr>
          </tbody>
        </table>
        {editable && (
          <button
            className="btn btn-sm m-3"
            onClick={() =>
              editRows((rs) => [...rs, { component: "", grade: "", function: "", share: "", tolerance: "", spec: "" }])
            }
          >
            {t("recipes.addRow")}
          </button>
        )}
      </section>

      <section className="card overflow-x-auto">
        <h2 className="text-base px-4 pt-3">{t("recipes.params")}</h2>
        <table className="w-full text-sm mt-2">
          <thead className="bg-surface-alt text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t("recipes.col.param")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.value")}</th>
              <th className="px-3 py-2 text-left">{t("recipes.col.control")}</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {params.map((p, i) => (
              <tr key={p.key} className="border-t border-border">
                {(["label", "value", "control"] as const).map((f) => (
                  <td key={f} className="px-3 py-2">
                    {editable ? (
                      <input
                        className={INPUT_CLS}
                        value={p[f]}
                        onChange={(e) =>
                          editParams((ps) => ps.map((x, k) => (k === i ? { ...x, [f]: e.target.value } : x)))
                        }
                      />
                    ) : (
                      p[f] || "—"
                    )}
                  </td>
                ))}
                {editable && (
                  <td className="px-2">
                    <button
                      className="btn btn-sm"
                      title={t("recipes.removeRow")}
                      onClick={() => editParams((ps) => ps.filter((_, k) => k !== i))}
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <button
            className="btn btn-sm m-3"
            onClick={() =>
              editParams((ps) => [...ps, { key: `p_${crypto.randomUUID().slice(0, 8)}`, label: "", value: "", control: "" }])
            }
          >
            {t("recipes.addRow")}
          </button>
        )}
      </section>

      <section className="card p-4 space-y-3">
        <label className="block">
          <span className="text-xs text-text-muted">{t("recipes.note")}</span>
          {editable ? (
            <textarea
              className={INPUT_CLS}
              rows={2}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setDirty(true);
              }}
            />
          ) : (
            <p className="text-sm whitespace-pre-line">{recipe.note || "—"}</p>
          )}
        </label>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-xs text-text-muted">{t("recipes.developed")}</dt>
            <dd>{signature(recipe.developer_name, recipe.developed_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-muted">{t("recipes.agreed")}</dt>
            <dd>{signature(recipe.agreer_name, recipe.agreed_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-muted">{t("recipes.approvedBy")}</dt>
            <dd>{signature(recipe.approver_name, recipe.approved_at)}</dd>
          </div>
        </dl>
        {error && (
          <ErrorBanner
            onDismiss={() => {
              save.reset();
              action.reset();
            }}
          >
            {errText(error)}
          </ErrorBanner>
        )}
        <div className="flex flex-wrap gap-2">
          {editable && (
            <button
              className="btn btn-sm btn-primary"
              disabled={!dirty || !name.trim() || badNumber || save.isPending}
              onClick={() => save.mutate()}
            >
              {t("recipes.save")}
            </button>
          )}
          {editable && !recipe.agreed_at && (
            <button className="btn btn-sm" disabled={dirty || action.isPending} onClick={() => action.mutate("agree")}>
              {t("recipes.agree")}
            </button>
          )}
          {recipe.status === "DRAFT" && hasPermission("recipe.approve") && (
            <button
              className="btn btn-sm btn-primary"
              disabled={dirty || action.isPending}
              onClick={() => action.mutate("approve")}
            >
              {t("recipes.approve")}
            </button>
          )}
          {recipe.status !== "DRAFT" && canManage && (
            <button className="btn btn-sm" disabled={action.isPending} onClick={() => action.mutate("newVersion")}>
              {t("recipes.newVersion")}
            </button>
          )}
          {recipe.status !== "ARCHIVED" && canManage && (
            <button
              className="btn btn-sm"
              disabled={action.isPending}
              onClick={() => window.confirm(t("recipes.archiveConfirm")) && action.mutate("archive")}
            >
              {t("recipes.archive")}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
